import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import {
    AccountDetail,
    AccountIssueReportsResponse,
    AccountListResponse,
    AccountSummary,
    ListAccountsArgs,
    UpdateAccountStatusArgs,
} from './customerAccountApiTypes';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

/**
 * Individual customers (end-user accounts) — read-only list slice.
 *
 * Tenant scoping is enforced server-side: `newBackendAxiosBaseQuery` routes
 * through `sendNewBackendApiRequest`, which injects the `X-Tenant` header
 * automatically, so the list returns tenant-scoped accounts only.
 *
 * Served by `new-backend/admin/src/controllers/customerAccountController.ts`:
 * `GET /accounts` (list), `GET /accounts?email=` (exact lookup, used by "add
 * member") and `GET /accounts/:id` (the detail view).
 *
 * Accounts are created by end users, never by tenant admins, so there is no
 * mutation here — and therefore no `invalidatesTags` to keep in sync.
 */
export const customerAccountApiSlice = createApi({
    reducerPath: 'customerAccountApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['accountList'],
    endpoints: (builder) => ({
        getAccounts: builder.query<AccountListResponse, ListAccountsArgs | void>({
            query: (args) => ({
                url: '/accounts',
                method: 'GET',
                params: args
                    ? {
                        ...(args.search ? { search: args.search } : {}),
                        ...(args.page !== undefined ? { page: args.page } : {}),
                        ...(args.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
                    }
                    : undefined,
            }),
            providesTags: ['accountList'],
        }),
        /**
         * One account, by id, for the detail page. Its own request rather than
         * a lookup into the list cache: the list is paginated (25 by default),
         * so an account past the first page is simply not in it — and a deep
         * link has no list to read from at all.
         */
        getAccount: builder.query<AccountDetail, { id: string }>({
            query: ({ id }) => ({
                url: `/accounts/${id}`,
                method: 'GET',
            }),
            providesTags: ['accountList'],
        }),
        /**
         * This account's issue reports (#2102 item 4). Its own request rather
         * than part of the detail payload: it is a list that can grow, and the
         * detail header should render without waiting for it.
         */
        getAccountIssueReports: builder.query<AccountIssueReportsResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/accounts/${id}/issue-reports`,
                method: 'GET',
            }),
            providesTags: ['accountList'],
        }),
        /**
         * Lock / unlock (#2102 item 5). Invalidates `accountList`, which BOTH
         * the list and the detail query provide — so the row's status and the
         * detail header both refresh, rather than one of them keeping the old
         * value until a reload.
         */
        updateAccountStatus: builder.mutation<AccountSummary, UpdateAccountStatusArgs>({
            query: ({ id, status }) => ({
                url: `/accounts/${id}/status`,
                method: 'PATCH',
                data: { status },
            }),
            invalidatesTags: ['accountList'],
        }),
    }),
});

export const {
    useGetAccountQuery,
    useGetAccountIssueReportsQuery,
    useGetAccountsQuery,
    useUpdateAccountStatusMutation,
} = customerAccountApiSlice;
